import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import ClientLayout from '../../components/ClientLayout';
import { useNavigate } from 'react-router-dom';

// --- CONFIGURACIÓN ---
const TIPOS_PRODUCTO = [
    { value: 'CAFE_AMERICANO', label: 'Café Americano Standard', keyPrecio: 'precio_cafe_americano' },
    { value: 'ESPECIALIDAD_CHIAPAS', label: 'Especialidad Chiapas (Altura)', keyPrecio: 'precio_especialidad_chiapas' },
    { value: 'ESPECIALIDAD_GUERRERO', label: 'Especialidad Guerrero', keyPrecio: 'precio_especialidad_guerrero' },
    { value: 'DESCAFEINADO', label: 'Descafeinado', keyPrecio: 'precio_descafeinado' },
    { value: 'CAFE_DE_OLLA', label: 'Mezcla Café de Olla', keyPrecio: 'precio_cafe_olla' },
    { value: 'CAFE_EJECUTIVO', label: 'Mezcla Ejecutiva', keyPrecio: 'precio_cafe_ejecutivo' }
];

const COLORS = { green: '#2E5B2E', pink: '#C19A6B', cream: '#F9F9F5', dark: '#333333', white: '#FFFFFF' };

function NuevoPedido() {
    const navigate = useNavigate();
    
    // Data del Sistema
    const [sucursales, setSucursales] = useState([]);
    const [perfilPrecios, setPerfilPrecios] = useState(null);
    
    // Formulario
    const [form, setForm] = useState({
        sucursalId: '',
        tipoProducto: 'CAFE_AMERICANO',
        cantidadGrano: 0,
        cantidadMolido: 0,
        fechaEntrega: '',
        notas: ''
    });

    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);

    // Cargar Datos Iniciales
    useEffect(() => {
        const loadData = async () => {
            const token = localStorage.getItem('token');
            if (!token) return navigate('/login');
            try {
                // Ejecutamos en paralelo para rapidez
                const [sucRes, profileRes] = await Promise.all([
                    api.get('/sucursales/me', { headers: { Authorization: `Bearer ${token}` } }),
                    api.get('/profile/me', { headers: { Authorization: `Bearer ${token}` } })
                ]);

                setSucursales(sucRes.data);
                setPerfilPrecios(profileRes.data);

                // Pre-seleccionar primera sucursal si existe
                if (sucRes.data.length > 0) {
                    setForm(prev => ({ ...prev, sucursalId: sucRes.data[0].id }));
                }

            } catch (error) {
                console.error("Error cargando datos", error);
                alert("Error de conexión. Revisa tu sesión.");
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [navigate]);

    // --- LÓGICA DE COTIZACIÓN ---
    const getPrecioUnitario = () => {
        if (!perfilPrecios) return 0;
        const productoInfo = TIPOS_PRODUCTO.find(p => p.value === form.tipoProducto);
        // Usamos la clave dinámica para sacar el precio del perfil
        return Number(perfilPrecios[productoInfo?.keyPrecio]) || 0;
    };

    const calcularTotal = () => {
        const precio = getPrecioUnitario();
        const kilos = Number(form.cantidadGrano) + Number(form.cantidadMolido);
        return (precio * kilos).toFixed(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (Number(form.cantidadGrano) + Number(form.cantidadMolido) <= 0) {
            return alert("Debes pedir al menos 1 Kg.");
        }
        if (!form.sucursalId) return alert("Selecciona una sucursal.");

        setSending(true);
        try {
            const token = localStorage.getItem('token');
            await api.post('/pedidos', form, { headers: { Authorization: `Bearer ${token}` } });
            alert("✅ Pedido realizado con éxito.");
            navigate('/bienvenida');
        } catch (error) {
            alert("Error al enviar el pedido.");
            setSending(false);
        }
    };

    if (loading) return <p style={{textAlign:'center', marginTop:50}}>Cargando cotizador...</p>;

    return (
        <ClientLayout title="Nuevo Pedido">
            <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', gap: 20, flexWrap: 'wrap-reverse' }}>
                
                {/* 1. FORMULARIO */}
                <div style={{ flex: '2 1 400px', background: COLORS.white, padding: 30, borderRadius: 12, boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                    <h2 style={{ color: COLORS.green, borderBottom: `1px solid ${COLORS.cream}`, paddingBottom: 10 }}>Configura tu Pedido</h2>
                    
                    <form onSubmit={handleSubmit}>
                        {/* Selector Sucursal */}
                        <div style={groupStyle}>
                            <label style={labelStyle}>📍 Sucursal de Entrega</label>
                            <select 
                                style={inputStyle} 
                                value={form.sucursalId} 
                                onChange={e => setForm({...form, sucursalId: e.target.value})}
                            >
                                {sucursales.map(s => <option key={s.id} value={s.id}>{s.nombre} - {s.ciudad}</option>)}
                            </select>
                        </div>

                        {/* Selector Producto */}
                        <div style={groupStyle}>
                            <label style={labelStyle}>☕ Variedad de Café</label>
                            <select 
                                style={inputStyle} 
                                value={form.tipoProducto} 
                                onChange={e => setForm({...form, tipoProducto: e.target.value})}
                            >
                                {TIPOS_PRODUCTO.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                            <small style={{color: COLORS.pink, fontWeight:'bold'}}>
                                Precio pactado: ${getPrecioUnitario().toFixed(2)} / Kg
                            </small>
                        </div>

                        {/* Cantidades */}
                        <div style={{ display: 'flex', gap: 15, marginBottom: 15 }}>
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Molido (Kg)</label>
                                <input type="number" min="0" step="0.5" style={inputStyle} 
                                    value={form.cantidadMolido} 
                                    onChange={e => setForm({...form, cantidadMolido: e.target.value})} 
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Grano (Kg)</label>
                                <input type="number" min="0" step="0.5" style={inputStyle} 
                                    value={form.cantidadGrano} 
                                    onChange={e => setForm({...form, cantidadGrano: e.target.value})} 
                                />
                            </div>
                        </div>

                        <div style={groupStyle}>
                            <label style={labelStyle}>📅 Fecha de Entrega</label>
                            <input type="date" required style={inputStyle} 
                                value={form.fechaEntrega} 
                                onChange={e => setForm({...form, fechaEntrega: e.target.value})} 
                            />
                        </div>

                        <div style={groupStyle}>
                            <label style={labelStyle}>📝 Notas Adicionales</label>
                            <textarea style={{...inputStyle, height: 80}} 
                                value={form.notas} 
                                onChange={e => setForm({...form, notas: e.target.value})} 
                                placeholder="Ej: Entregar después de las 10am..."
                            />
                        </div>

                        <button disabled={sending} type="submit" style={btnStyle}>
                            {sending ? 'Procesando...' : 'Confirmar Pedido'}
                        </button>
                    </form>
                </div>

                {/* 2. RESUMEN DE COSTOS (Cotización en vivo) */}
                <div style={{ flex: '1 1 250px', height: 'fit-content', background: COLORS.green, color: COLORS.white, padding: 25, borderRadius: 12, boxShadow: '0 8px 20px rgba(46, 91, 46, 0.3)' }}>
                    <h3 style={{ marginTop: 0, borderBottom: '1px solid #ffffff50', paddingBottom: 10 }}>Resumen</h3>
                    
                    <div style={rowStyle}>
                        <span>Precio/Kg:</span>
                        <strong>${getPrecioUnitario().toFixed(2)}</strong>
                    </div>
                    <div style={rowStyle}>
                        <span>Total Kilos:</span>
                        <strong>{(Number(form.cantidadGrano) + Number(form.cantidadMolido)).toFixed(1)} Kg</strong>
                    </div>
                    
                    <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px dashed #ffffff80', fontSize: 24, textAlign: 'center' }}>
                        <div style={{ fontSize: 14, textTransform: 'uppercase', marginBottom: 5 }}>Total Estimado</div>
                        <strong>${calcularTotal()}</strong>
                        <div style={{ fontSize: 12, fontWeight: 'normal', marginTop: 5 }}>MXN</div>
                    </div>
                </div>

            </div>
        </ClientLayout>
    );
}

// Estilos locales
const groupStyle = { marginBottom: 15 };
const labelStyle = { display: 'block', marginBottom: 5, fontWeight: 'bold', color: COLORS.dark };
const inputStyle = { width: '100%', padding: 12, borderRadius: 8, border: `1px solid ${COLORS.pink}50`, fontSize: 16 };
const btnStyle = { width: '100%', padding: 15, background: COLORS.green, color: 'white', border: 'none', borderRadius: 8, fontSize: 18, fontWeight: 'bold', cursor: 'pointer', marginTop: 10 };
const rowStyle = { display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 15 };

export default NuevoPedido;